import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './hooks/useAuth';
import { apiFetch } from './utils/api';
import {
  MAX_LENGTHS,
  validateText,
  useCappedPaste,
  getRemaining,
  escapeForDisplay,
} from './utils/inputValidation';
import MarketingSuggestions from './components/MarketingSuggestions';
import './MarketingAIPage.css';

const PRODUCT_TYPES = ['Serum', 'Cream', 'Cleanser', 'Toner', 'Essence', 'Mask', 'Sunscreen', 'Lip', 'Eye', 'Body'];
const SKIN_TYPES = ['Oily', 'Dry', 'Combination', 'Sensitive', 'Normal'];
const SKIN_GOALS = ['Hydration', 'Brightening', 'Anti-Aging', 'Acne Control', 'Firming', 'Pore Care', 'Even Tone', 'Soothing', 'UV Protection'];
const CHANNELS = ['Product Label', 'Social Media Post', 'Ad Copy', 'Video Script', 'Website Description', 'Email Marketing'];
const MARKETS = ['US (FDA)', 'Korea (MFDS)', 'EU (EC 1223/2009)'];
const TONES = ['Clinical/Dermatological', 'Luxury/Prestige', 'Natural/Clean', 'K-Beauty', 'Trendy/Gen-Z', 'Professional/Esthetician'];
const DEMOGRAPHICS = ['Teen (13-19)', 'Young Adult (20-29)', 'Adult (30-44)', 'Mature (45-59)', 'Senior (60+)'];
const GENDERS = ['Female', 'Male', 'Unisex'];
const BRAND_POS = ['Premium', 'Mass Market', 'Indie/Clean Beauty', 'Medical-Grade', 'K-Beauty Export'];
const LANGUAGES = [{ value: 'en', label: 'English' }, { value: 'ko', label: 'Korean' }, { value: 'bilingual', label: 'Bilingual' }];
const SEASONALS = ['Summer', 'Winter', 'Holiday', 'K-Beauty Trend', 'Clean Beauty Trend'];

export default function MarketingAIPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('generate');

  // Form state — Tier 1
  const [productType, setProductType] = useState('Serum');
  const [ingredients, setIngredients] = useState('');
  const [skinType, setSkinType] = useState('Normal');
  const [skinGoals, setSkinGoals] = useState([]);
  const [channel, setChannel] = useState('Product Label');
  const [market, setMarket] = useState('US (FDA)');
  const [language, setLanguage] = useState('en');

  // Tier 2
  const [tone, setTone] = useState('');
  const [demographic, setDemographic] = useState('');
  const [gender, setGender] = useState('');
  const [brandPos, setBrandPos] = useState('');

  // Tier 3
  const [emphasisKw, setEmphasisKw] = useState('');
  const [exclusionKw, setExclusionKw] = useState('');
  const [seasonal, setSeasonal] = useState('');
  const [count, setCount] = useState(5);

  // Results state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  // Phase 6: shared paste-truncation toast.
  const [pasteToast, setPasteToast] = useState('');

  // Phase 6 sign-off: free-text fields use Phase 5 profiles ai_marketing_long / _short / _keyword.
  const ingResult       = validateText(ingredients, 'ai_marketing_long');
  // Emphasis / exclusion are optional on the form; skip required-check when empty.
  const emphasisResult  = emphasisKw.trim() === ''  ? { ok: true } : validateText(emphasisKw,  'ai_marketing_short');
  const exclusionResult = exclusionKw.trim() === '' ? { ok: true } : validateText(exclusionKw, 'ai_marketing_short');

  const ingCounterVisible      = ingredients.length >= Math.floor(MAX_LENGTHS.ai_marketing_long  * 0.8);
  const emphasisCounterVisible = emphasisKw.length  >= Math.floor(MAX_LENGTHS.ai_marketing_short * 0.8);
  const exclusionCounterVisible= exclusionKw.length >= Math.floor(MAX_LENGTHS.ai_marketing_short * 0.8);

  const showToast = useCallback((origLen, cap) => {
    setPasteToast(`Pasted content truncated to ${cap} characters (was ${origLen}).`);
    window.clearTimeout(window.__ccasMaiPasteTimer);
    window.__ccasMaiPasteTimer = window.setTimeout(() => setPasteToast(''), 5000);
  }, []);
  const onIngPaste       = useCappedPaste(MAX_LENGTHS.ai_marketing_long,  (n) => showToast(n, MAX_LENGTHS.ai_marketing_long));
  const onEmphasisPaste  = useCappedPaste(MAX_LENGTHS.ai_marketing_short, (n) => showToast(n, MAX_LENGTHS.ai_marketing_short));
  const onExclusionPaste = useCappedPaste(MAX_LENGTHS.ai_marketing_short, (n) => showToast(n, MAX_LENGTHS.ai_marketing_short));

  // History / Favorites state
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [historyDetail, setHistoryDetail] = useState(null);

  // --- Multi-select helper ---
  const toggleGoal = (goal) => {
    setSkinGoals(prev =>
      prev.includes(goal) ? prev.filter(g => g !== goal) : [...prev, goal]
    );
  };

  // --- Generate ---
  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setResult(null);

    // Phase 6: pre-flight client validation. Server is still the source of truth.
    if (!ingResult.ok) {
      setError(ingResult.message || 'Invalid ingredients.');
      setLoading(false);
      return;
    }
    if (emphasisKw.trim() !== '' && !emphasisResult.ok) {
      setError(emphasisResult.message || 'Invalid emphasis keywords.');
      setLoading(false);
      return;
    }
    if (exclusionKw.trim() !== '' && !exclusionResult.ok) {
      setError(exclusionResult.message || 'Invalid exclusion keywords.');
      setLoading(false);
      return;
    }

    const ingList = ingredients
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    if (ingList.length === 0) {
      setError('Please enter at least one ingredient.');
      setLoading(false);
      return;
    }

    // Phase 6: per-split keyword cap (ai_marketing_keyword = 50 chars).
    const splitFields = [
      ['ingredients',         ingList],
      ['emphasis_keywords',   emphasisKw.split(',').map(s => s.trim()).filter(Boolean)],
      ['exclusion_keywords',  exclusionKw.split(',').map(s => s.trim()).filter(Boolean)],
    ];
    for (const [label, items] of splitFields) {
      for (const item of items) {
        const r = validateText(item, 'ai_marketing_keyword');
        if (!r.ok) {
          setError(`${label}: "${item}" — ${r.message}`);
          setLoading(false);
          return;
        }
      }
    }

    const body = {
      product_type: productType,
      ingredients: ingList,
      skin_type: skinType,
      skin_goals: skinGoals.length > 0 ? skinGoals : ['Hydration'],
      output_channel: channel,
      market,
      language,
      count,
    };
    if (tone) body.tone = tone;
    if (demographic) body.demographic = demographic;
    if (gender) body.gender = gender;
    if (brandPos) body.brand_positioning = brandPos;
    if (emphasisKw.trim()) body.emphasis_keywords = emphasisKw.split(',').map(s => s.trim()).filter(Boolean);
    if (exclusionKw.trim()) body.exclusion_keywords = exclusionKw.split(',').map(s => s.trim()).filter(Boolean);
    if (seasonal) body.seasonal = seasonal;

    const controller = new AbortController();
    const abortTimeout = setTimeout(() => controller.abort(), 60000);
    try {
      const res = await apiFetch('/api/v1/marketing/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const data = await res.json();
      if (!res.ok) {
        // Phase 6: render server INPUT_* envelope verbatim. No auto-retry on 429.
        const raw = data?.detail?.message || data?.detail?.error || data?.detail || 'Generation failed';
        throw new Error(escapeForDisplay(typeof raw === 'string' ? raw : JSON.stringify(raw)));
      }
      setResult(data);
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again with fewer suggestions.');
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      clearTimeout(abortTimeout);
      setLoading(false);
    }
  };

  // --- History ---
  const loadHistory = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/marketing/history?limit=20');
      const data = await res.json();
      if (res.ok) setHistory(data);
    } catch {}
  }, []);

  const loadHistoryDetail = async (id) => {
    try {
      const res = await apiFetch(`/api/v1/marketing/history/${id}`);
      const data = await res.json();
      if (res.ok) setHistoryDetail(data);
    } catch {}
  };

  // --- Favorites ---
  const loadFavorites = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/marketing/favorites?limit=50');
      const data = await res.json();
      if (res.ok) setFavorites(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (tab === 'history') loadHistory();
    if (tab === 'favorites') loadFavorites();
  }, [tab, loadHistory, loadFavorites]);

  // --- Render ---
  return (
    <div className="mai-page">
      {/* Header */}
      <div className="mai-header">
        <h1>Marketing AI</h1>
      </div>

      {/* Tabs */}
      <div className="mai-tabs">
        <button className={`mai-tab ${tab === 'generate' ? 'active' : ''}`} onClick={() => setTab('generate')}>Generate</button>
        <button className={`mai-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => { setTab('history'); setHistoryDetail(null); }}>History</button>
        <button className={`mai-tab ${tab === 'favorites' ? 'active' : ''}`} onClick={() => setTab('favorites')}>Favorites</button>
      </div>

      {/* Error */}
      {error && (
        <div className="mai-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* =================== GENERATE TAB =================== */}
      {tab === 'generate' && (
        <>
          <div className="mai-form">
            {/* Section 1: Product & Ingredients */}
            <div className="mai-section">
              <h3>🧴 Product & Ingredients</h3>
              <div className="mai-field">
                <label>Product Type</label>
                <select value={productType} onChange={e => setProductType(e.target.value)}>
                  {PRODUCT_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Key Ingredients (comma-separated)</label>
                <input
                  type="text"
                  value={ingredients}
                  onChange={e => setIngredients(e.target.value)}
                  onPaste={onIngPaste}
                  maxLength={MAX_LENGTHS.ai_marketing_long}
                  placeholder="e.g. Hyaluronic Acid, Niacinamide, Vitamin C"
                />
                {ingCounterVisible && (
                  <div className="mai-counter" style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                    {getRemaining(ingredients, 'ai_marketing_long')} characters left
                  </div>
                )}
                {ingredients.length > 0 && !ingResult.ok && (
                  <div className="mai-error" style={{ fontSize: '0.75rem' }}>{ingResult.message}</div>
                )}
              </div>
            </div>

            {/* Section 2: Skin Profile */}
            <div className="mai-section">
              <h3>🧬 Skin Profile</h3>
              <div className="mai-field">
                <label>Skin Type</label>
                <select value={skinType} onChange={e => setSkinType(e.target.value)}>
                  {SKIN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Skin Goals</label>
                <div className="mai-chips">
                  {SKIN_GOALS.map(g => (
                    <button
                      key={g}
                      className={`mai-chip ${skinGoals.includes(g) ? 'selected' : ''}`}
                      onClick={() => toggleGoal(g)}
                      type="button"
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Section 3: Output Settings */}
            <div className="mai-section">
              <h3>📢 Output Settings</h3>
              <div className="mai-field">
                <label>Output Channel</label>
                <select value={channel} onChange={e => setChannel(e.target.value)}>
                  {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Regulatory Market</label>
                <select value={market} onChange={e => setMarket(e.target.value)}>
                  {MARKETS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Language</label>
                <select value={language} onChange={e => setLanguage(e.target.value)}>
                  {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Tone / Style</label>
                <select value={tone} onChange={e => setTone(e.target.value)}>
                  <option value="">— Auto —</option>
                  {TONES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Number of Suggestions</label>
                <select value={count} onChange={e => setCount(Number(e.target.value))}>
                  {[3, 5, 7, 10].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            {/* Section 4: Advanced */}
            <div className="mai-section">
              <h3>⚙️ Advanced (Optional)</h3>
              <div className="mai-field">
                <label>Target Demographic</label>
                <select value={demographic} onChange={e => setDemographic(e.target.value)}>
                  <option value="">— Any —</option>
                  {DEMOGRAPHICS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Gender</label>
                <select value={gender} onChange={e => setGender(e.target.value)}>
                  <option value="">— Any —</option>
                  {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Brand Positioning</label>
                <select value={brandPos} onChange={e => setBrandPos(e.target.value)}>
                  <option value="">— Any —</option>
                  {BRAND_POS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Seasonal / Trend</label>
                <select value={seasonal} onChange={e => setSeasonal(e.target.value)}>
                  <option value="">— None —</option>
                  {SEASONALS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="mai-field">
                <label>Emphasis Keywords (comma-separated)</label>
                <input
                  type="text"
                  value={emphasisKw}
                  onChange={e => setEmphasisKw(e.target.value)}
                  onPaste={onEmphasisPaste}
                  maxLength={MAX_LENGTHS.ai_marketing_short}
                  placeholder="e.g. glow, radiance"
                />
                {emphasisCounterVisible && (
                  <div className="mai-counter" style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                    {getRemaining(emphasisKw, 'ai_marketing_short')} characters left
                  </div>
                )}
                {emphasisKw.length > 0 && !emphasisResult.ok && (
                  <div className="mai-error" style={{ fontSize: '0.75rem' }}>{emphasisResult.message}</div>
                )}
              </div>
              <div className="mai-field">
                <label>Exclusion Keywords (comma-separated)</label>
                <input
                  type="text"
                  value={exclusionKw}
                  onChange={e => setExclusionKw(e.target.value)}
                  onPaste={onExclusionPaste}
                  maxLength={MAX_LENGTHS.ai_marketing_short}
                  placeholder="e.g. paraben, fragrance"
                />
                {exclusionCounterVisible && (
                  <div className="mai-counter" style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                    {getRemaining(exclusionKw, 'ai_marketing_short')} characters left
                  </div>
                )}
                {exclusionKw.length > 0 && !exclusionResult.ok && (
                  <div className="mai-error" style={{ fontSize: '0.75rem' }}>{exclusionResult.message}</div>
                )}
              </div>
            </div>
          </div>

          {/* Generate button */}
          <div className="mai-generate-bar">
            {pasteToast && (
              <div className="mai-toast" style={{ fontSize: '0.8rem', color: '#D97706', marginBottom: 8 }}>{pasteToast}</div>
            )}
            <button
              className="mai-generate-btn"
              onClick={handleGenerate}
              disabled={loading || !ingResult.ok || !emphasisResult.ok || !exclusionResult.ok}
            >
              {loading ? 'Generating…' : '✨ Generate Suggestions'}
            </button>
          </div>

          {/* Loading */}
          {loading && (
            <div className="mai-loading">
              <div className="mai-spinner" />
              <div>Generating and validating suggestions…</div>
              <div style={{ fontSize: '0.85rem', marginTop: 4, color: 'var(--text-tertiary)' }}>
                Each suggestion is checked against the compliance pipeline
              </div>
            </div>
          )}

          {/* Results */}
          {result && result.suggestions && (
            <MarketingSuggestions
              suggestions={result.suggestions}
              onFavoriteChange={() => {
                // Reload the generation detail to refresh favorite states
                if (result.generation_id) {
                  apiFetch(`/api/v1/marketing/history/${result.generation_id}`)
                    .then(r => r.json())
                    .then(d => {
                      if (d.suggestions) setResult(prev => ({ ...prev, suggestions: d.suggestions }));
                    })
                    .catch(() => {});
                }
              }}
            />
          )}
        </>
      )}

      {/* =================== HISTORY TAB =================== */}
      {tab === 'history' && !historyDetail && (
        <div>
          {history.length === 0 ? (
            <div className="mai-empty">No generation history yet. Generate your first suggestions!</div>
          ) : (
            <div className="mai-history-list">
              {history.map(h => {
                const criteria = h.criteria || {};
                return (
                  <div key={h.id} className="mai-history-item" onClick={() => loadHistoryDetail(h.id)}>
                    <div className="mai-history-top">
                      <span className="mai-history-channel">{criteria.output_channel || h.output_channel}</span>
                      <span className="mai-history-date">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
                    </div>
                    <div className="mai-history-criteria">
                      {criteria.product_type} · {(criteria.ingredients || []).join(', ')} · {criteria.skin_type}
                    </div>
                    <div className="mai-history-count">{h.suggestion_count} suggestions</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'history' && historyDetail && (
        <div>
          <button className="mai-bulk-btn" onClick={() => setHistoryDetail(null)} style={{ marginBottom: 16 }}>
            ← Back to history
          </button>
          <MarketingSuggestions
            suggestions={historyDetail.suggestions || []}
            onFavoriteChange={() => loadHistoryDetail(historyDetail.id)}
          />
        </div>
      )}

      {/* =================== FAVORITES TAB =================== */}
      {tab === 'favorites' && (
        <div>
          {favorites.length === 0 ? (
            <div className="mai-empty">No bookmarked suggestions yet.</div>
          ) : (
            <div className="mai-cards">
              {favorites.map((f, idx) => (
                <div key={f.id} className="mai-card">
                  <div className="mai-card-top">
                    <div className="mai-card-text">{f.text}</div>
                    <div className="mai-card-actions">
                      <button
                        className="mai-card-btn"
                        onClick={async () => {
                          try { await navigator.clipboard.writeText(f.text); } catch {}
                        }}
                        title="Copy"
                      >📋</button>
                      <button
                        className="mai-card-btn favorited"
                        onClick={async () => {
                          try {
                            await apiFetch(`/api/v1/marketing/favorites/${f.suggestion_id}`, { method: 'DELETE' });
                            loadFavorites();
                          } catch {}
                        }}
                        title="Remove bookmark"
                      >★</button>
                    </div>
                  </div>
                  <div className="mai-card-meta">
                    <span className={`mai-badge ${f.compliance_status}`}>
                      {f.compliance_status === 'safe' ? '✅ Safe' : f.compliance_status === 'caution' ? '⚠️ Caution' : '❌ Rejected'}
                    </span>
                    <span className="mai-badge-channel">{f.channel_type}</span>
                  </div>
                  {f.notes && <div className="mai-fav-note">Note: {f.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
